import React, { useCallback, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CollapseBlock, Spinner } from '../../../../../common/index.jsx';
import { routes } from '../../../../../../utils/helpers/ApiClient.jsx';
import { AppBarCMS } from '../../../../../basic/index.jsx';
import DatesTable from '../../../../../common/FriOpen/Dates/DatesTable/DatesTable.jsx';
import {
  useDeleteDateMutation,
  useGetDatesQuery,
  useGetMyDatesQuery,
  useSignIntoDateMutation,
  useToggleDateEnabledMutation,
} from '../../../../../../redux/services/fo_dates.jsx';
import { useGetWorkshopQuery } from '../../../../../../redux/services/fo_workshops.jsx';
import { useSelector } from 'react-redux';
import {
  ROLE_FRI_OPEN_ADMIN,
  ROLE_FRI_OPEN_PARTICIPANT,
} from '../../../../../../utils/constants/roles.js';
import useUserHasRole from '../../../../../../hooks/useUserHasRoles.jsx';
import { useToastMessages } from '../../../../../../hooks/useToastMessages';

const FoDatesPage = ({ mode }) => {
  const { workshopId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const { deleteSuccess, successMessage, apiErrorMessage } = useToastMessages();

  const lastActionRef = useRef(null);

  const isAdmin = useUserHasRole(ROLE_FRI_OPEN_ADMIN);
  const isFriOpenParticipant = useUserHasRole(ROLE_FRI_OPEN_PARTICIPANT);
  const hideDateDetailAction = Boolean(user) && isFriOpenParticipant;

  const isMyDates = mode === 'my';
  const isWorkshop = mode === 'workshop';

  const {
    data: dates,
    isLoading: isLoadingWorkshopDates,
    refetch: refetchWorkshopDates,
  } = useGetDatesQuery(workshopId, {
    skip: !isWorkshop,
  });

  const {
    data: myDates,
    isLoading: isLoadingMyDates,
    refetch: refetchMyDates,
  } = useGetMyDatesQuery(undefined, {
    skip: !isMyDates,
  });

  const visibleMyDates = (myDates || []).filter(
    (date) => !date?.workshop?.event?.archived_at,
  );

  const {
    data: workshop,
    isLoading: isLoadingWorkshop,
  } = useGetWorkshopQuery(workshopId, {
    skip: !isWorkshop,
  });

  const [deleteDate, { isSuccess: isSuccessDelete }] = useDeleteDateMutation();
  const [signIntoDate, { isSuccess: isSuccessSign }] = useSignIntoDateMutation();
  const [toggleDateEnabled] = useToggleDateEnabledMutation();

  const isArchivedWorkshopEvent = Boolean(workshop?.event?.archived_at);

  const handleEditClick = useCallback(
    (date) => {
      const isAuthor = Number(user?.person_id) === Number(date?.creator_id);
      const isLecturer = Number(user?.person_id) === Number(date?.lecturer_id);
      if (isAuthor || isLecturer || isAdmin) {
        navigate(routes.cmsFoDatesEdit(date.id));
      }
    },
    [user?.person_id, isAdmin, navigate],
  );

  const handleRowClick = useCallback(
    (date) => {
      if (date?.workshop?.event?.archived_at) {
        navigate(routes.cmsFoDatesDetail(date.id));
        return;
      }

      handleEditClick(date);
    },
    [handleEditClick, navigate],
  );

  const handleSign = (record, action) => {
    lastActionRef.current = action;
    signIntoDate({
      dateId: record.id,
      action,
    });
  };

  const handleToggleEnabled = useCallback(
    async (date) => {
      try {
        await toggleDateEnabled(date.id).unwrap();
        successMessage({
          message:
            date?.is_enabled === false
              ? t('Termín bol obnovený')
              : t('Termín bol zrušený'),
        });
      } catch (error) {
        apiErrorMessage(error);
      }
    },
    [toggleDateEnabled, successMessage, apiErrorMessage, t],
  );

  useEffect(() => {
    if (isSuccessDelete) {
      deleteSuccess();
      isWorkshop && refetchWorkshopDates();
      isMyDates && refetchMyDates();
    }
  }, [isSuccessDelete]);

  useEffect(() => {
    if (isSuccessSign) {
      if (lastActionRef.current === 'register') {
        successMessage({
          message: t('Boli ste úspešne prihlásený na termín'),
        });
      } else if (lastActionRef.current === 'unregister') {
        successMessage({
          message: t('Boli ste odhlásený z termínu'),
        });
      }
    }
  }, [isSuccessSign]);

  const tableProps = {
    onEditClick: handleEditClick,
    onRowClick: handleRowClick,
    onDetailClick: (date) => navigate(routes.cmsFoDatesDetail(date.id)),
    isAdmin,
  };

  const handleCopyClick = useCallback(
    (date) => {
      const query = new URLSearchParams({ copyFromDateId: String(date.id) });
      navigate(`${routes.cmsFoDatesCreate(workshopId)}?${query.toString()}`);
    },
    [navigate, workshopId],
  );

  if (isWorkshop) {
    return isLoadingWorkshopDates || isLoadingWorkshop ? (
      <Spinner />
    ) : (
      <>
        <AppBarCMS
          title={`${t('Zoznam termínov pre FRI seminár')}${workshop?.name ? ` - ${workshop.name}` : ''}`}
          backUrl={-1}
          backUrlTooltip={t('Späť')}
          createText={isArchivedWorkshopEvent ? undefined : t('Pridať termín')}
          createUrl={
            isArchivedWorkshopEvent ? undefined : routes.cmsFoDatesCreate(workshopId)
          }
          permissionsBasic={'fo-dates'}
        />
        <div style={{ margin: '10px' }}>
          <CollapseBlock>
            <DatesTable
              {...tableProps}
              dates={dates}
              onDetailClick={
                hideDateDetailAction ? undefined : tableProps.onDetailClick
              }
              onParticipantsClick={(date) =>
                navigate(routes.cmsFoDateParticipants(date.id))
              }
              onEditClick={isArchivedWorkshopEvent ? undefined : tableProps.onEditClick}
              onRowClick={
                isArchivedWorkshopEvent
                  ? (date) => navigate(routes.cmsFoDatesDetail(date.id))
                  : tableProps.onRowClick
              }
              onDelete={isArchivedWorkshopEvent ? undefined : (date) => deleteDate(date.id)}
              onCopyClick={isArchivedWorkshopEvent ? undefined : handleCopyClick}
              onToggleEnabled={isArchivedWorkshopEvent ? undefined : handleToggleEnabled}
              onSignOnClick={(date) => handleSign(date, 'register')}
              onUnsignOnClick={(date) => handleSign(date, 'unregister')}
              isArchivedParent={isArchivedWorkshopEvent}
            />
          </CollapseBlock>
        </div>
      </>
    );
  }

  return isLoadingMyDates ? (
    <Spinner />
  ) : (
    <>
      <AppBarCMS
        title={t(
          isFriOpenParticipant
            ? t('Zoznam mojich termínov')
            : t('Zoznam termínov lektora FRI akcie'),
        )}
        backUrl={-1}
        backUrlTooltip={t('Späť')}
      />
      <div style={{ margin: '10px' }}>
        <CollapseBlock>
          <DatesTable
            {...tableProps}
            dates={visibleMyDates}
            isMyDates={isMyDates}
            onToggleEnabled={handleToggleEnabled}
            onUnsignOnClick={(date) => handleSign(date, 'unregister')}
          />
        </CollapseBlock>
      </div>
    </>
  );
};

export default FoDatesPage;
